/**
 * Health Dashboard Widget Definitions
 *
 * Widgets for cross-client health monitoring of project delivery,
 * infrastructure budgets, and support metrics.
 *
 * Access: AWS and Partner users only
 */

import {
  createMetricWidget,
  createChartWidget,
  createListWidget,
  WIDGET_SIZES,
  DATA_SOURCES,
  WIDGET_ICONS,
} from '../widgetTemplates';

// Use string directly to avoid circular dependency with widgetRegistry
const WIDGET_CATEGORY_HEALTH = 'Health';

const {
  FiCheckCircle,
  FiAlertTriangle,
  FiDollarSign,
  FiLifeBuoy,
  FiGrid,
  FiXCircle,
  FiCalendar,
  FiClock,
  FiBarChart2,
  FiAlertOctagon,
  FiPieChart,
} = WIDGET_ICONS;

/**
 * 1. On-Time Delivery Rate Widget
 * Percentage of projects on track (green health status)
 */
export const onTimeDeliveryRateWidget = createMetricWidget({
  id: 'on-time-delivery-rate',
  name: 'On-Time Delivery Rate',
  description: 'Percentage of projects on schedule (green health)',
  icon: FiCheckCircle,
  category: WIDGET_CATEGORY_HEALTH,
  dataSource: DATA_SOURCES.HEALTH,
  respectsClientFilter: false, // Cross-client view
  refreshRate: 60000,
  getData: (sourceData) => {
    const rate = sourceData?.deliveryMetrics?.on_time_rate || 0;
    let onTimeColor: string;
    if (rate >= 80) onTimeColor = 'green';
    else if (rate >= 60) onTimeColor = 'yellow';
    else onTimeColor = 'red';
    return {
      value: rate,
      label: 'On-Time Rate',
      format: 'percentage',
      color: onTimeColor,
      link: '/hub/projects',
    };
  },
});

/**
 * 2. Projects Behind Schedule Widget
 * Count of projects at risk (yellow/red health status)
 */
export const projectsBehindScheduleWidget = createMetricWidget({
  id: 'projects-behind-schedule',
  name: 'Projects Behind Schedule',
  description: 'Count of projects at risk (yellow/red)',
  icon: FiAlertTriangle,
  category: WIDGET_CATEGORY_HEALTH,
  dataSource: DATA_SOURCES.HEALTH,
  respectsClientFilter: false,
  refreshRate: 60000,
  getData: (sourceData) => {
    const count = sourceData?.projectsAtRisk?.count || 0;
    let atRiskColor: string;
    if (count === 0) atRiskColor = 'green';
    else if (count <= 5) atRiskColor = 'yellow';
    else atRiskColor = 'red';
    return {
      value: count,
      label: 'At Risk',
      format: 'number',
      color: atRiskColor,
      link: '/hub/projects',
    };
  },
});

/**
 * 3. Budget Burn Rate Widget
 * Overall infrastructure budget utilization percentage
 */
export const budgetBurnRateWidget = createMetricWidget({
  id: 'budget-burn-rate',
  name: 'Budget Burn Rate',
  description: 'Overall infrastructure budget utilization',
  icon: FiDollarSign,
  category: WIDGET_CATEGORY_HEALTH,
  dataSource: DATA_SOURCES.HEALTH,
  respectsClientFilter: false,
  refreshRate: 60000,
  getData: (sourceData) => {
    const utilization = sourceData?.budgetMetrics?.utilization_pct || 0;
    let budgetColor: string;
    if (utilization >= 90) budgetColor = 'red';
    else if (utilization >= 75) budgetColor = 'yellow';
    else budgetColor = 'green';
    return {
      value: utilization,
      label: 'Budget Used',
      format: 'percentage',
      color: budgetColor,
      link: '/hub/projects',
    };
  },
});

/**
 * 4. Pending Support Requests Widget
 * Count of open/pending support tickets
 */
export const pendingSupportRequestsWidget = createMetricWidget({
  id: 'pending-support-requests',
  name: 'Support Tickets',
  description: 'Open and pending support requests',
  icon: FiLifeBuoy,
  category: WIDGET_CATEGORY_HEALTH,
  dataSource: DATA_SOURCES.HEALTH,
  respectsClientFilter: false,
  refreshRate: 60000,
  getData: (sourceData) => {
    const count = sourceData?.supportMetrics?.pending_count || 0;
    let pendingTicketsColor: string;
    if (count <= 5) pendingTicketsColor = 'green';
    else if (count <= 10) pendingTicketsColor = 'yellow';
    else pendingTicketsColor = 'red';
    return {
      value: count,
      label: 'Pending Tickets',
      format: 'number',
      color: pendingTicketsColor,
      link: '/hub/support',
    };
  },
});

/**
 * 5. Project Health Heatmap Widget
 * Treemap visualization of project health across all accessible clients
 * MOVED from hubWidgets.js
 */
export const projectHealthHeatmapWidget = {
  id: 'project-health-heatmap',
  name: 'Project Health Map',
  type: 'custom',
  description: 'Heatmap showing project health across all clients',
  icon: FiGrid,
  category: WIDGET_CATEGORY_HEALTH,
  defaultSize: WIDGET_SIZES.LARGE,
  dataSource: DATA_SOURCES.HEALTH,
  respectsClientFilter: false, // NEVER filtered - always shows all accessible clients
  supportsDateFilter: false,
  refreshRate: 300000,

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getData: (sourceData: Record<string, any>) => {
    if (!sourceData?.heatmapData) {
      return null;
    }
    return sourceData.heatmapData;
  },
};

/**
 * 6. Budget Overrun Alert Widget
 * Projects over budget - count and total overrun amount
 */
export const budgetOverrunAlertWidget = createMetricWidget({
  id: 'budget-overrun-alert',
  name: 'Budget Overruns',
  description: 'Projects exceeding their budget',
  icon: FiXCircle,
  category: WIDGET_CATEGORY_HEALTH,
  dataSource: DATA_SOURCES.HEALTH,
  respectsClientFilter: false,
  refreshRate: 60000,
  getData: (sourceData) => {
    const count = sourceData?.budgetMetrics?.overrun_count || 0;
    const amount = sourceData?.budgetMetrics?.overrun_amount || 0;
    return {
      value: count,
      label: 'Projects Over Budget',
      format: 'number',
      color: count > 0 ? 'red' : 'green',
      subtitle: count > 0 ? `$${amount.toLocaleString()} total overrun` : 'All projects on budget',
      link: '/hub/projects',
    };
  },
});

/**
 * 7. All Projects by Health List Widget
 * All active projects sorted by criticality (red, yellow, green)
 */
export const criticalProjectsListWidget = createListWidget({
  id: 'critical-projects-list',
  name: 'Projects by Health',
  description: 'All active projects sorted by health status',
  icon: FiGrid,
  category: WIDGET_CATEGORY_HEALTH,
  dataSource: DATA_SOURCES.HEALTH,
  respectsClientFilter: false,
  refreshRate: 60000,
  getData: (sourceData) => {
    const critical = sourceData?.projectsAtRisk?.critical || [];
    const warning = sourceData?.projectsAtRisk?.warning || [];

    // Combine and sort: critical (red) first, then warning (yellow)
    const allProjects = [
      ...critical.map((project: { name?: string; client_name?: string; days_behind?: number; id?: string; project_id?: string; client_id?: string }) => ({
        title: project.name || 'Project',
        subtitle: project.client_name || '',
        value: project.days_behind ? `${Math.round(project.days_behind)} days behind` : 'Off track',
        status: 'red',
        id: project.id || project.project_id,
        link: project.client_id && (project.id || project.project_id)
          ? `/hub/projects?client=${project.client_id}&project=${project.id || project.project_id}`
          : undefined,
      })),
      ...warning.map((project: { name?: string; client_name?: string; days_behind?: number; id?: string; project_id?: string; client_id?: string }) => ({
        title: project.name || 'Project',
        subtitle: project.client_name || '',
        value: project.days_behind ? `${Math.round(project.days_behind)} days behind` : 'At risk',
        status: 'yellow',
        id: project.id || project.project_id,
        link: project.client_id && (project.id || project.project_id)
          ? `/hub/projects?client=${project.client_id}&project=${project.id || project.project_id}`
          : undefined,
      }))
    ];

    return {
      items: allProjects,
      emptyMessage: 'No projects at risk - all on track!',
    };
  },
});

/**
 * 8. Milestone Completion Rate Widget
 * Percentage of milestones completed
 */
export const milestoneCompletionRateWidget = createMetricWidget({
  id: 'milestone-completion-rate',
  name: 'Milestone Progress',
  description: 'Percentage of milestones completed',
  icon: FiCalendar,
  category: WIDGET_CATEGORY_HEALTH,
  dataSource: DATA_SOURCES.HEALTH,
  respectsClientFilter: false,
  refreshRate: 60000,
  getData: (sourceData) => {
    const rate = sourceData?.deliveryMetrics?.milestone_completion_rate || 0;
    return {
      value: rate,
      label: 'Completed',
      format: 'percentage',
      color: rate >= 80 ? 'green' : 'yellow',
      link: '/hub/projects',
    };
  },
});

/**
 * 9. Average Project Delay Widget
 * Average days behind for at-risk projects
 */
export const avgProjectDelayWidget = createMetricWidget({
  id: 'avg-project-delay',
  name: 'Avg Project Delay',
  description: 'Average days behind schedule for at-risk projects',
  icon: FiCalendar,
  category: WIDGET_CATEGORY_HEALTH,
  dataSource: DATA_SOURCES.HEALTH,
  respectsClientFilter: false,
  refreshRate: 60000,
  getData: (sourceData) => {
    const avgDays = sourceData?.deliveryMetrics?.avg_days_behind || 0;
    let avgDaysColor: string;
    if (avgDays === 0) avgDaysColor = 'green';
    else if (avgDays <= 7) avgDaysColor = 'yellow';
    else avgDaysColor = 'red';
    return {
      value: Math.round(avgDays),
      label: 'Days Behind',
      format: 'custom',
      formatValue: (val: number) => val > 0 ? `${val} days` : 'On track',
      color: avgDaysColor,
      link: '/hub/projects',
    };
  },
});

/**
 * 10. Total Active Projects Widget
 * Count of all in-progress and planning projects
 */
export const totalActiveProjectsWidget = createMetricWidget({
  id: 'total-active-projects',
  name: 'Active Projects',
  description: 'Total projects in progress or planning',
  icon: FiBarChart2,
  category: WIDGET_CATEGORY_HEALTH,
  dataSource: DATA_SOURCES.HEALTH,
  respectsClientFilter: false,
  refreshRate: 60000,
  getData: (sourceData) => {
    const total = sourceData?.deliveryMetrics?.total_projects || 0;
    return {
      value: total,
      label: 'Active Projects',
      format: 'number',
      color: 'blue',
      link: '/hub/projects',
    };
  },
});

/**
 * 11. Infrastructure Spend by Client Widget
 * Bar chart showing infrastructure spending by client
 */
export const infrastructureSpendByClientWidget = createChartWidget({
  id: 'infrastructure-spend-by-client',
  name: 'Spend by Client',
  description: 'Infrastructure spending breakdown by client',
  icon: FiBarChart2,
  category: WIDGET_CATEGORY_HEALTH,
  dataSource: DATA_SOURCES.HEALTH,
  respectsClientFilter: false,
  refreshRate: 60000,
  getData: (sourceData) => {
    const byClient = sourceData?.budgetMetrics?.spend_by_client || [];
    return {
      data: byClient.map((client: { client_name?: string; total_spent?: number }) => ({
        name: client.client_name || 'Unknown',
        value: client.total_spent || 0,
      })),
      chartType: 'bar',
      xKey: 'name',
      yKey: 'value',
      formatValue: (value: number) => `$${(value / 1000).toFixed(1)}K`,
      link: '/hub/projects',
    };
  },
});

/**
 * 11. Budget Alerts List Widget
 * Projects at 80%+ budget utilization
 */
export const budgetAlertsListWidget = createListWidget({
  id: 'budget-alerts-list',
  name: 'Projects Near Budget Limit',
  description: 'Projects approaching budget limits (80%+)',
  icon: FiAlertOctagon,
  category: WIDGET_CATEGORY_HEALTH,
  dataSource: DATA_SOURCES.HEALTH,
  respectsClientFilter: false,
  refreshRate: 60000,
  getData: (sourceData) => {
    const alerts = sourceData?.budgetMetrics?.alerts || [];
    return {
      items: alerts.map((alert: { project_name?: string; client_name?: string; utilization_pct?: number; project_id?: string; client_id?: string }) => ({
        title: alert.project_name || 'Project',
        subtitle: alert.client_name || '',
        value: `${Math.round(alert.utilization_pct || 0)}% used`,
        status: (alert.utilization_pct ?? 0) >= 90 ? 'red' : 'yellow',
        id: alert.project_id,
        link: alert.client_id && alert.project_id
          ? `/hub/projects?client=${alert.client_id}&project=${alert.project_id}`
          : undefined,
      })),
      emptyMessage: 'No budget concerns - all projects healthy!',
    };
  },
});

/**
 * 12. Open Support Requests List Widget
 * List of all open and pending support tickets
 */
export const openSupportRequestsListWidget = createListWidget({
  id: 'open-support-requests-list',
  name: 'Open Support Requests',
  description: 'All open and pending support tickets',
  icon: FiLifeBuoy,
  category: WIDGET_CATEGORY_HEALTH,
  dataSource: DATA_SOURCES.HEALTH,
  respectsClientFilter: false,
  refreshRate: 60000,
  getData: (sourceData) => {
    const requests = sourceData?.supportMetrics?.open_requests || [];
    return {
      items: requests.map((request: { subject?: string; client_name?: string; priority?: string; id?: string; client_id?: string }) => {
        let requestStatus: string;
        if (request.priority === 'High') requestStatus = 'red';
        else if (request.priority === 'Medium') requestStatus = 'yellow';
        else requestStatus = 'green';
        return {
        title: request.subject || 'Support Request',
        subtitle: request.client_name || '',
        value: request.priority || 'Normal',
        status: requestStatus,
        id: request.id,
        link: request.client_id && request.id
          ? `/hub/support?client=${request.client_id}&ticket=${request.id}`
          : `/hub/support`,
        };
      }),
      emptyMessage: 'No open support requests!',
    };
  },
});

/**
 * 13. Funding by Program Widget - Health Version
 * Distribution of funding across AWS programs (cross-client aggregation)
 */
export const fundingByProgramHealthWidget = createChartWidget({
  id: 'funding-by-program-health',
  name: 'Funding by Program',
  description: 'Distribution of funding by AWS program across all clients',
  icon: FiPieChart,
  category: WIDGET_CATEGORY_HEALTH,
  dataSource: DATA_SOURCES.HEALTH,
  respectsClientFilter: false,
  refreshRate: 60000,
  getData: (sourceData) => {
    const programs = sourceData?.fundingByProgram?.programs || [];
    return {
      data: programs.map((p: { name?: string; total_amount?: number }) => ({
        name: p.name || 'Unknown',
        value: p.total_amount || 0,
      })),
      chartType: 'pie',
      dataKey: 'value',
      link: '/hub/funding',
    };
  },
});

/**
 * 14. Credits by Client Widget - Health Version
 * Credit allocation breakdown across all accessible clients
 */
export const creditsByClientHealthWidget = createChartWidget({
  id: 'credits-by-client-health',
  name: 'Credits by Client',
  description: 'Credit allocation breakdown across all accessible clients',
  icon: FiBarChart2,
  category: WIDGET_CATEGORY_HEALTH,
  dataSource: DATA_SOURCES.HEALTH,
  respectsClientFilter: false,
  refreshRate: 60000,
  getData: (sourceData) => {
    const clients = sourceData?.creditsByClient?.clients || [];
    return {
      data: clients,
      chartType: 'bar',
      xKey: 'client_name',
      yKey: 'total_credits',
      formatValue: (value: number) => `$${(value / 1000).toFixed(1)}K`,
      link: '/hub/funding',
    };
  },
});

/**
 * 15. Credit Expiration Timeline Widget - Health Version
 * Credit expiration timeline across all accessible clients
 */
export const creditExpirationHealthWidget = createChartWidget({
  id: 'credit-expiration-health',
  name: 'Credit Expiration Timeline',
  description: 'Credits expiring over next 12 months across all accessible clients',
  icon: FiClock,
  category: WIDGET_CATEGORY_HEALTH,
  dataSource: DATA_SOURCES.HEALTH,
  respectsClientFilter: false,
  refreshRate: 60000,
  getData: (sourceData) => {
    const timeline = sourceData?.creditExpiration?.timeline || [];
    return {
      data: timeline,
      chartType: 'line',
      xKey: 'month',
      yKey: 'amount',
      formatValue: (value: number) => `$${(value / 1000).toFixed(1)}K`,
      link: '/hub/funding',
    };
  },
});

/**
 * 16. Recent Funding Requests Widget - Health Version
 * Recent funding requests across all accessible clients with navigation
 */
export const fundingRequestsHealthWidget = createListWidget({
  id: 'funding-requests-health',
  name: 'Recent Funding Requests',
  description: 'Recent funding requests across all accessible clients',
  icon: FiDollarSign,
  category: WIDGET_CATEGORY_HEALTH,
  dataSource: DATA_SOURCES.HEALTH,
  respectsClientFilter: false,
  refreshRate: 60000,
  getData: (sourceData) => {
    const requests = sourceData?.fundingRequests?.requests || [];
    return {
      items: requests.map((req: { name?: string; client_name?: string; status?: string; requested_amount?: number; id?: string; application_id?: string }) => {
        let fundingStatus: string;
        if (req.status === 'APPROVED') fundingStatus = 'green';
        else if (req.status === 'REJECTED') fundingStatus = 'red';
        else fundingStatus = 'yellow';
        return {
          title: req.name || 'Funding Request',
          subtitle: req.client_name || '',
          value: req.requested_amount ? `$${req.requested_amount.toLocaleString()}` : '',
          status: fundingStatus,
          id: req.id || req.application_id,
          link: `/hub/funding?request=${req.id || req.application_id}`,
        };
      }),
      emptyMessage: 'No recent funding requests',
    };
  },
});

// Export all health widgets as an array
export const HEALTH_WIDGETS = [
  onTimeDeliveryRateWidget,
  projectsBehindScheduleWidget,
  budgetBurnRateWidget,
  pendingSupportRequestsWidget,
  projectHealthHeatmapWidget,
  budgetOverrunAlertWidget,
  criticalProjectsListWidget,
  milestoneCompletionRateWidget,
  avgProjectDelayWidget,
  totalActiveProjectsWidget,
  infrastructureSpendByClientWidget,
  budgetAlertsListWidget,
  openSupportRequestsListWidget,
  fundingByProgramHealthWidget,
  creditsByClientHealthWidget,
  creditExpirationHealthWidget,
  fundingRequestsHealthWidget,
];

// Export as a map for quick lookup
export const HEALTH_WIDGETS_MAP = HEALTH_WIDGETS.reduce((map: Record<string, any>, widget) => {
  map[widget.id] = widget;
  return map;
}, {} as Record<string, any>);
